import React from 'react';

import Layout from '../../../Layout';
import Menuable from '../../../Menuable';
import TextCell from './TextCell';
import type { TextCellMenuOption } from './TextCell';
import type { IconName } from 'shared';

export interface CellDropdownProps {
  value: string | null;
  menuOptions: TextCellMenuOption[];
  onMenuSelect: (value: string) => void;
  menuPlaceholder?: string;
  /** When set, show avatar leading the trigger (e.g. for Owner column). */
  avatarUrl?: string | null;
  defaultIcon?: IconName;
  padding?: number;
  /** When set, use compact left padding = cellPadding - trigger left padding (8 with avatar, 12 without) so hover pill aligns. */
  cellPadding?: number;
}

const MENU_MIN_WIDTH = 160;
const MENU_MAX_HEIGHT = 280;
const TRIGGER_PADDING_LEFT_AVATAR = 8;
const TRIGGER_PADDING_LEFT_DEFAULT = 12;

const CellDropdown: React.FC<CellDropdownProps> = ({
  value,
  menuOptions,
  onMenuSelect,
  menuPlaceholder,
  avatarUrl,
  defaultIcon,
  padding = 0,
  cellPadding
}) => {
  const menuableOptions = menuOptions.map(opt => ({
    label: opt.label,
    onClick: () => onMenuSelect(opt.value)
  }));

  const hasAvatar = avatarUrl !== undefined || defaultIcon !== undefined;
  const triggerPaddingLeft = hasAvatar ? TRIGGER_PADDING_LEFT_AVATAR : TRIGGER_PADDING_LEFT_DEFAULT;
  const compactLeftPadding = cellPadding !== undefined ? Math.max(0, cellPadding - triggerPaddingLeft) : undefined;

  const layoutPadding =
    compactLeftPadding !== undefined
      ? { paddingLeft: compactLeftPadding, paddingRight: padding, paddingY: padding }
      : { padding };

  return (
    <Layout {...layoutPadding} marginLeft={-triggerPaddingLeft} width="100%">
      <div role="presentation" onClick={e => e.stopPropagation()}>
        <Menuable
          options={menuableOptions}
          itemTextColor="gray-600"
          menuProps={{
            minWidth: MENU_MIN_WIDTH,
            maxHeight: MENU_MAX_HEIGHT,
            overflowY: 'auto'
          }}
        >
          {({ isOpen }) => (
            <TextCell
              value={value}
              dropdownTrigger
              dropdownTriggerVariant="pill"
              menuOptions={menuOptions}
              menuPlaceholder={menuPlaceholder}
              avatarUrl={avatarUrl}
              defaultIcon={defaultIcon}
              isOpen={isOpen}
            />
          )}
        </Menuable>
      </div>
    </Layout>
  );
};

export default CellDropdown;
